import type { YearMonth } from './types';

/**
 * device / software の契約・費用画面（cost / payment）で使う「対象月（target month）」ベースの期間ロジック。
 *
 * URL は単月 `YYYYMM`（= 対象月）を持ち、対象月を中央に前 {@link MONTHS_BEFORE} ヶ月 〜
 * 後 {@link MONTHS_AFTER} ヶ月の計 {@link WINDOW_MONTHS} ヶ月を表示する。
 *
 * payroll は from_to（`period.ts`）を継続利用するため、本モジュールは device / software 専用。
 * API は「開始年月(ISO) + durationMonths」の既存契約をそのまま使い、値だけを対象月基準で算出する。
 */

/** 表示する月数（対象月を中央に前後 4 ヶ月ずつ）。 */
export const WINDOW_MONTHS = 9;
/** 対象月より前に表示する月数（残りの WINDOW_MONTHS - 1 - MONTHS_BEFORE ヶ月を後方に表示）。 */
const MONTHS_BEFORE = 4;
function padMonth(month: number): string {
  return String(month).padStart(2, '0');
}

/** URL パラメータ（`YYYYMM`）から対象年月を取り出す。 */
export function parseTargetMonth(value: string): YearMonth {
  return {
    year: Number.parseInt(value.slice(0, 4), 10),
    month: Number.parseInt(value.slice(4, 6), 10)
  };
}

/**
 * URL パラメータが対象月として妥当か（`YYYYMM` 形式かつ月が 1〜12）を判定する。
 * `$targetMonth` は任意文字列を受け取り得るため、不正値（形式不一致・月が範囲外）だと
 * 期間ロジックが壊れる。ルートの beforeLoad でこれを検証し、不正なら
 * {@link getDefaultTargetMonth} へリダイレクトする。
 */
export function isValidTargetMonth(value: string): boolean {
  if (!/^\d{6}$/.test(value)) return false;
  const month = Number.parseInt(value.slice(4, 6), 10);
  return month >= 1 && month <= 12;
}

/** 対象年月を URL パラメータ（`YYYYMM`）に整形する。 */
export function formatTargetMonth(year: number, month: number): string {
  return `${year}${padMonth(month)}`;
}

/** 既定の対象年月（当月）。リダイレクト初期値・フォールバックに使う。 */
export function getDefaultTargetMonth(): string {
  const now = new Date();
  return formatTargetMonth(now.getFullYear(), now.getMonth() + 1);
}

/** 対象年月から delta ヶ月ずらした `YYYYMM` を返す（前月送り = -1 / 翌月送り = +1）。 */
export function stepTargetMonth(value: string, delta: number): string {
  const {
    year,
    month
  } = parseTargetMonth(value);
  const date = new Date(year, month - 1 + delta, 1);
  return formatTargetMonth(date.getFullYear(), date.getMonth() + 1);
}

/**
 * 対象月を中央に、前 {@link MONTHS_BEFORE} ヶ月 〜 後 4 ヶ月の
 * 計 {@link WINDOW_MONTHS} 件の年月配列（列ヘッダ用）。
 */
export function getMonthsForTarget(value: string): YearMonth[] {
  const {
    year,
    month
  } = parseTargetMonth(value);
  const months: YearMonth[] = [];
  for (let i = 0; i < WINDOW_MONTHS; i++) {
    const date = new Date(year, month - 1 - MONTHS_BEFORE + i, 1);
    months.push({
      year: date.getFullYear(),
      month: date.getMonth() + 1
    });
  }
  return months;
}

/**
 * 表示ウィンドウ開始月（対象月 - {@link MONTHS_BEFORE}）の ISO 文字列。
 * API の `startYearMonth` / `targetYearMonth`（ウィンドウ先頭のアンカー）に渡す。
 */
export function targetWindowStartIso(value: string): string {
  const {
    year,
    month
  } = parseTargetMonth(value);
  return new Date(Date.UTC(year, month - 1 - MONTHS_BEFORE, 1)).toISOString();
}